import React, { useState } from 'react';
import { ShieldAlert, Trash2, CheckCircle2, AlertCircle, Phone, ArrowLeft, Info, HelpCircle } from 'lucide-react';
import apiClient from '../../services/api';
import './DeleteAccount.css';

const DeleteAccount = () => {
    const [phone, setPhone] = useState('');
    const [reason, setReason] = useState('No longer using the app');
    const [confirmed, setConfirmed] = useState(false);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!phone.trim()) {
            setMessage({ type: 'error', text: 'Please enter your registered phone number.' });
            return;
        }

        if (!confirmed) {
            setMessage({ type: 'error', text: 'Please confirm that you understand account deletion is permanent.' });
            return;
        }

        setLoading(true);
        setMessage(null);

        try {
            const response = await apiClient.post('/users/request-deletion', {
                phone: phone.trim(),
                reason
            });

            if (response.data?.success) {
                setMessage({
                    type: 'success',
                    text: response.data.message || 'Your account and associated personal data have been permanently deleted.'
                });
                setPhone('');
                setConfirmed(false);
            } else {
                setMessage({
                    type: 'error',
                    text: response.data?.message || 'Failed to delete account. Please check your details.'
                });
            }
        } catch (error) {
            const errText = error.response?.data?.message || 'An error occurred while requesting account deletion. Please verify your phone number.';
            setMessage({ type: 'error', text: errText });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="delete-account-container">
            <div className="delete-account-header">
                <div className="brand-logo">
                    <img src="/somstay_logo.png" alt="Somstay Logo" onError={(e) => { e.target.style.display = 'none'; }} />
                    <span className="brand-title">Somstay</span>
                </div>
                <a href="/" className="back-link">
                    <ArrowLeft size={16} /> Back to Somstay
                </a>
            </div>

            <div className="delete-account-card">
                <div className="card-badge">
                    <ShieldAlert size={28} className="shield-icon" />
                </div>
                
                <h1 className="card-title">Delete Your Somstay Account</h1>
                <p className="card-subtitle">
                    Request permanent deletion of your Somstay user account and associated personal data below.
                </p>

                {message && (
                    <div className={`alert-box ${message.type}`}>
                        {message.type === 'success' ? <CheckCircle2 size={20} /> : <AlertCircle size={20} />}
                        <span>{message.text}</span>
                    </div>
                )}

                {message?.type === 'success' ? (
                    <div className="success-state">
                        <h3>Account Deleted Successfully</h3>
                        <p>All your personal profile data, wishlist items, saved preferences, and account records matching <strong>{phone}</strong> have been removed from our active servers.</p>
                        <a href="/" className="btn-primary">Return to Somstay</a>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="deletion-form">
                        <div className="form-group">
                            <label htmlFor="phone">
                                <Phone size={16} /> Phone Number *
                            </label>
                            <input
                                id="phone"
                                type="tel"
                                className="form-input"
                                placeholder="e.g. +252 61 XXX XXXX or 063XXXXXXX"
                                value={phone}
                                onChange={(e) => setPhone(e.target.value)}
                                required
                            />
                            <span className="input-hint">Enter the phone number associated with your Somstay account.</span>
                        </div>

                        <div className="form-group">
                            <label htmlFor="reason">
                                <HelpCircle size={16} /> Reason for Deletion *
                            </label>
                            <select
                                id="reason"
                                className="form-select"
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                required
                            >
                                <option value="No longer using the app">No longer using the app</option>
                                <option value="Created duplicate account">Created duplicate account</option>
                                <option value="Privacy concerns">Privacy concerns</option>
                                <option value="App functionality issues">App functionality issues</option>
                                <option value="Other">Other</option>
                            </select>
                        </div>

                        <div className="confirmation-box">
                            <input
                                type="checkbox"
                                id="confirmCheckbox"
                                checked={confirmed}
                                onChange={(e) => setConfirmed(e.target.checked)}
                            />
                            <label htmlFor="confirmCheckbox">
                                I confirm that I want to permanently delete my Somstay account and associated personal data.
                            </label>
                        </div>

                        <button
                            type="submit"
                            className="btn-danger"
                            disabled={loading || !confirmed}
                        >
                            {loading ? (
                                <span className="spinner">Deleting Account...</span>
                            ) : (
                                <>
                                    <Trash2 size={18} /> Permanently Delete Account
                                </>
                            )}
                        </button>
                    </form>
                )}

                <div className="info-accordion">
                    <h3><Info size={18} /> Data Deletion & Policy Summary</h3>
                    
                    <div className="info-section">
                        <h4>What is deleted:</h4>
                        <ul>
                            <li>Your user profile, name, phone number, and account credentials.</li>
                            <li>Your saved wishlist items, favorites, and notification tokens.</li>
                            <li>All active login sessions.</li>
                        </ul>
                    </div>

                    <div className="info-section">
                        <h4>Official Web Domain:</h4>
                        <p>
                            This deletion request page is accessible via <strong style={{ color: '#38bdf8' }}>somstay.app/delete-account</strong> for Google Play policy compliance.
                        </p>
                    </div>
                </div>
            </div>
            
            <div className="delete-account-footer">
                <p>&copy; {new Date().getFullYear()} Somstay. All rights reserved.</p>
                <p>Support contact: <a href="mailto:support@somstay.app">support@somstay.app</a></p>
            </div>
        </div>
    );
};

export default DeleteAccount;
